import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type { BillHistoryEntryResponse } from "@/services/core-api/types"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"

interface GetBillHistoryParams {
  billId: number
  organizationId: Organization["id"]
}

const getBillHistory = async ({
  billId,
  organizationId,
}: GetBillHistoryParams): Promise<BillHistoryEntryResponse[]> => {
  try {
    const response = await httpClient.get<BillHistoryEntryResponse[]>(
      `/api/v1/organizations/${organizationId}/bills/${billId}/history`
    )

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseBillHistoryOptions {
  billId: number | null
  organizationId: Organization["id"]
  queryOptions?: Omit<
    UseQueryOptions<BillHistoryEntryResponse[], HttpError>,
    "queryKey" | "queryFn"
  >
}

export const useBillHistory = ({
  billId,
  organizationId,
  queryOptions = {},
}: UseBillHistoryOptions) => {
  const queryKey = useQueryKey(
    ApiQueryName.Bills,
    "getMany",
    billId === null ? null : String(billId),
    {
      endpoint: "history",
      organizationId,
    }
  )

  const queryResult = useQuery<BillHistoryEntryResponse[], HttpError>({
    ...queryOptions,
    enabled:
      Boolean(organizationId) &&
      billId !== null &&
      (queryOptions.enabled ?? true),
    queryFn: async () => {
      if (billId === null) {
        throw new Error("billId is required to fetch bill history")
      }
      return getBillHistory({ billId, organizationId })
    },
    queryKey,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Bills)
}
